import { useQuery, useQueryClient, useQueries } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useSnackbar } from "notistack";

import { useLoadingScreen } from '../../Store/LoadingManager';

import QueryBuilderHelper from "../../Utilities/Helpers/QueryBuilderHelper";
import DropdownOptionsHelper from "../../Utilities/Helpers/DropdownOptionsHelper";

import { CachingEnum } from '../../Utilities/Enums/CachingEnum';

import { DeleteCompanyService, CreateCompanyService, EditCompanyService, GetCompanies, GetPublicCompanies, GetCompanyById, GetCompanyByRegistrationCode, GetCompanyByVendorRegistrationCode, GenerateCompanyDatabaseService } from "../../Api/Service/Company/companyService";

import { ErrorMessage, SuccessMessage } from "../../Constants/Messages";

export function useCompanyHook(q_queryKey: any, params: any, runInitialFetch: boolean = true, useCompanyPublicList: boolean = false) {
    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();
    const fileBaseUrl = `${process.env.REACT_APP_API_URL}/api/`;
    const DropdownList = [CachingEnum.COUNTRY_LIST];
    let { loading, setLoading } = useLoadingScreen();

    // Initial fetch
    const { data, fetchStatus } = useQuery({
        queryKey: q_queryKey,
        queryFn: () => getCompaniesList(params),
        enabled: runInitialFetch,
    });

    const { data: DropdownData, pending: dropdownFetchStatus } = useQueries({
        queries: DropdownList.map((item) => {
            return {
                queryKey: [item],
                queryFn: () => DropdownOptionsHelper.getLists(item),
            }
        }),
        combine: (results) => {
            const dictionary = Object.assign({}, ...results.map((x, index) => ({ [DropdownList[index]]: x.data })));
            return {
                data: dictionary,
                pending: results.some((result) => result.isPending),
            }
        },
    })


    const getCompaniesList = async (params: any) => {
        let _data;
        const responseData = (!useCompanyPublicList ? await GetCompanies(QueryBuilderHelper.BuildQueryString(params)) : await GetPublicCompanies(QueryBuilderHelper.BuildQueryString(params)));
        if (responseData?.isSuccess) {
            _data = responseData?.data
        } else {
            enqueueSnackbar(responseData?.message ?? (responseData?.data?.message ?? ErrorMessage.PROBLEM_FETCHING_DATA), { variant: 'error' })
        }
        return _data;
    }

    const getCompanyById = async (id: string | number) => {
        setLoading(true);

        let _data;
        const responseData = await GetCompanyById(id);

        setLoading(false);
        
        return responseData?.data ?? null;
    }

    const getCompanyByRegistrationCode = async (id: string | number) => {
        setLoading(true);

        let _data;
        const responseData = await GetCompanyByRegistrationCode(id);

        setLoading(false);
        
        return responseData?.data ?? null;
    }

    const getCompanyByVendorRegistrationCode = async (id: string | number) => {
        setLoading(true);

        let _data;
        const responseData = await GetCompanyByVendorRegistrationCode(id);

        setLoading(false);
        
        return responseData?.data ?? null;
    }

    const createCompany = async (params: any) => {
        setLoading(true);
        const responseData = await CreateCompanyService(params);

        if (responseData?.isSuccess) {
            queryClient.invalidateQueries(q_queryKey);
            enqueueSnackbar(responseData?.data?.message ?? "Company " + SuccessMessage.CREATED_SUCCESSFULLY, { variant: 'success' })
        } else {
            enqueueSnackbar(responseData?.message ?? (responseData?.data?.message ?? ErrorMessage.PROBLEM_CREATING_DATA), { variant: 'error' })
        }

        setLoading(false);

        return responseData;
    }

    const editCompanyById = async (id: any, params: any) => {
        setLoading(true);

        const responseData = await EditCompanyService(id, params);
        
        if (responseData?.isSuccess) {
            queryClient.invalidateQueries(q_queryKey)
            enqueueSnackbar(responseData?.data?.message ?? "Company " + SuccessMessage.UPDATED_SUCCESSFULLY, { variant: 'success' })
        } else {
            enqueueSnackbar(responseData?.message ?? (responseData?.data?.message ?? ErrorMessage.PROBLEM_UPDATING_DATA), { variant: 'error' })
        }

        setLoading(false);

        return responseData;
    }

    const deleteCompanyById = async (id: any, remarks?: any) => {
        setLoading(true);
        
        const responseData = await DeleteCompanyService(id, JSON.stringify({remarks: remarks ?? ""}));
        
        if (responseData?.isSuccess) {
            queryClient.invalidateQueries(q_queryKey)
            enqueueSnackbar(responseData?.data?.message ?? "Company " + SuccessMessage.DELETED_SUCCESSFULLY, { variant: 'success' })
        } else {
            enqueueSnackbar(responseData?.message ?? (responseData?.data?.message ?? ErrorMessage.PROBLEM_DELETING_DATA), { variant: 'error' })
        }

        setLoading(false);

        return responseData;
    }

    const generateCompanyDatabase = async (id: any) => {
        setLoading(true);
        const responseData = await GenerateCompanyDatabaseService(id);

        if (responseData?.isSuccess) {
            queryClient.invalidateQueries(q_queryKey);
            enqueueSnackbar(responseData?.data?.message ?? "Company " + SuccessMessage.DATABASE_GENERATED_SUCCESSFULLY, { variant: 'success' })
        } else {
            enqueueSnackbar(responseData?.message ?? (responseData?.data?.message ?? ErrorMessage.PROBLEM_GENERATING_DATABASE), { variant: 'error' })
        }

        setLoading(false);

        return responseData;
        
    }

    const companyInvalidateQueries = async () => {
        queryClient.invalidateQueries(q_queryKey);
        return true;
    }

    return { data, DropdownData, fetchStatus, dropdownFetchStatus, getCompanyById, getCompanyByRegistrationCode, getCompanyByVendorRegistrationCode, createCompany, editCompanyById, deleteCompanyById, generateCompanyDatabase, companyInvalidateQueries }
}